import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { API } from '@/App';
import axios from 'axios';
import { Search, Briefcase, CheckCircle2, XCircle, Eye, Star, Clock, Users, Building2 } from 'lucide-react';

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [functionFilter, setFunctionFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, [filter, functionFilter]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      if (functionFilter !== 'all') params.append('function', functionFilter);
      
      const response = await axios.get(`${API}/admin/projects?${params.toString()}`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateProjectStatus = async (projectId, newStatus, isFeatured = null) => {
    setUpdating(true);
    try {
      const params = new URLSearchParams();
      if (newStatus) params.append('status', newStatus);
      if (isFeatured !== null) params.append('is_featured', isFeatured);
      
      await axios.put(`${API}/admin/projects/${projectId}?${params.toString()}`);
      toast.success('Project updated');
      fetchProjects();
      setSelectedProject(null);
    } catch (error) {
      toast.error('Failed to update project');
    } finally {
      setUpdating(false);
    }
  };

  const filteredProjects = projects.filter(p => {
    if (search) {
      const searchLower = search.toLowerCase();
      return p.title?.toLowerCase().includes(searchLower) || 
             p.company_name?.toLowerCase().includes(searchLower);
    }
    return true;
  });

  const statusColors = {
    pending: 'badge-warning',
    active: 'badge-success',
    paused: 'badge-neutral',
    filled: 'badge-primary',
    closed: 'badge-error',
  };

  const functionColors = {
    product: 'badge-primary',
    design: 'badge-accent',
    engineering: 'bg-emerald-100 text-emerald-700',
  };

  return (
    <AdminLayout>
      <div data-testid="admin-projects-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="heading-2 mb-2">Manage Projects</h1>
            <p className="text-slate-600">{filteredProjects.length} projects</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6" data-testid="project-filters">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by title or company..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="filled">Filled</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={functionFilter} onValueChange={setFunctionFilter}>
              <SelectTrigger className="w-40" data-testid="function-filter">
                <SelectValue placeholder="Function" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Functions</SelectItem>
                <SelectItem value="product">Product</SelectItem>
                <SelectItem value="design">Design</SelectItem>
                <SelectItem value="engineering">Engineering</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Projects Table */}
        <Card className="overflow-hidden" data-testid="projects-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Project</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Company</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Function</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Applicants</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      <td colSpan={6} className="px-6 py-4">
                        <div className="animate-pulse h-4 bg-slate-200 rounded"></div>
                      </td>
                    </tr>
                  ))
                ) : filteredProjects.length > 0 ? (
                  filteredProjects.map((project) => (
                    <tr key={project.id} className="hover:bg-slate-50" data-testid={`project-row-${project.id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                            <Briefcase className="h-5 w-5 text-slate-500" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900">{project.title}</p>
                              {project.is_featured && <Star className="h-4 w-4 text-amber-500 fill-current" />}
                            </div>
                            <p className="text-sm text-slate-500">{project.hours_per_week}h/week · {project.duration}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{project.company_name}</td>
                      <td className="px-6 py-4">
                        <span className={`${functionColors[project.function]} px-2 py-0.5 rounded-full text-xs`}>
                          {project.function}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{project.applicant_count || 0}</td>
                      <td className="px-6 py-4">
                        <span className={`${statusColors[project.status]} text-xs`}>{project.status}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => setSelectedProject(project)}
                            data-testid={`view-project-${project.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {project.status === 'pending' && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                onClick={() => updateProjectStatus(project.id, 'active')}
                                data-testid={`approve-project-${project.id}`}
                              >
                                <CheckCircle2 className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => updateProjectStatus(project.id, 'closed')}
                                data-testid={`reject-project-${project.id}`}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                      No projects found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Project Detail Modal */}
      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="project-detail-modal">
          <DialogHeader>
            <DialogTitle>Project Details</DialogTitle>
          </DialogHeader>
          
          {selectedProject && (
            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-slate-900">{selectedProject.title}</h3>
                  {selectedProject.is_featured && <Star className="h-4 w-4 text-amber-500 fill-current" />}
                </div>
                <p className="text-sm text-slate-500 flex items-center">
                  <Building2 className="h-4 w-4 mr-1" />
                  {selectedProject.company_name}
                </p>
                <span className={`${statusColors[selectedProject.status]} text-xs mt-2 inline-block`}>
                  {selectedProject.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Function</span>
                  <p className="font-medium">{selectedProject.function}</p>
                </div>
                <div>
                  <span className="text-slate-500">Duration</span>
                  <p className="font-medium">{selectedProject.duration}</p>
                </div>
                <div>
                  <span className="text-slate-500">Hours/Week</span>
                  <p className="font-medium">{selectedProject.hours_per_week}</p>
                </div>
                <div>
                  <span className="text-slate-500">Location</span>
                  <p className="font-medium capitalize">{selectedProject.location_type}</p>
                </div>
                <div>
                  <span className="text-slate-500">Budget</span>
                  <p className="font-medium">{selectedProject.budget_range || 'Not set'}</p>
                </div>
                <div>
                  <span className="text-slate-500">Applicants</span>
                  <p className="font-medium">{selectedProject.applicant_count || 0}</p>
                </div>
              </div>

              {selectedProject.description && (
                <div>
                  <span className="text-sm text-slate-500">Description</span>
                  <p className="text-sm text-slate-700 mt-1 line-clamp-4">{selectedProject.description}</p>
                </div>
              )}

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900">Featured Project</p>
                  <p className="text-sm text-slate-500">Show on homepage</p>
                </div>
                <Switch
                  checked={selectedProject.is_featured}
                  onCheckedChange={(checked) => updateProjectStatus(selectedProject.id, null, checked)}
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                {selectedProject.status === 'pending' && (
                  <Button
                    onClick={() => updateProjectStatus(selectedProject.id, 'active')}
                    disabled={updating}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Approve & Activate
                  </Button>
                )}
                {selectedProject.status === 'active' && (
                  <Button
                    variant="outline"
                    onClick={() => updateProjectStatus(selectedProject.id, 'paused')}
                    disabled={updating}
                  >
                    Pause
                  </Button>
                )}
                {selectedProject.status === 'paused' && (
                  <Button
                    onClick={() => updateProjectStatus(selectedProject.id, 'active')}
                    disabled={updating}
                  >
                    Reactivate
                  </Button>
                )}
                {selectedProject.status !== 'closed' && selectedProject.status !== 'filled' && (
                  <Button
                    variant="destructive"
                    onClick={() => updateProjectStatus(selectedProject.id, 'closed')}
                    disabled={updating}
                  >
                    Close
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminProjects;
